<vs-dialog *ngIf="document" [config]="dialogConfig">
  <vs-dialog-header [config]="dialogHeaderConfig"></vs-dialog-header>

  <div class="pt-4 pb-2 px-2 overflow-hidden">
    <form class="w-[480px] max-w-full flex flex-col gap-5" [formGroup]="form">
      <vs-form-field
        *ngIf="hideStatusBadge !== true"
        appearance="horizontal"
        class="[&>div>div:first-child]:max-w-[96px]"
        [config]="{ label: 'Status' }"
      >
        <vs-storage-item-status-badge [item]="document"></vs-storage-item-status-badge>
      </vs-form-field>

      <vs-form-field
        *ngIf="hideVisibilityInput !== true"
        appearance="horizontal"
        class="[&>div>div:first-child]:max-w-[96px]"
        [config]="{ label: 'Visibility' }"
      >
        <div class="flex gap-4">
          <div class="flex items-center gap-2">
            <input vsInput id="radio-input-private" type="radio" value="private" formControlName="visibility" />
            <label vsRadioLabel for="radio-input-private">Private</label>
          </div>

          <div class="flex items-center gap-2">
            <input vsInput id="radio-input-public" type="radio" value="public" formControlName="visibility" />
            <label vsRadioLabel for="radio-input-public">Public</label>
          </div>
        </div>
      </vs-form-field>

      <vs-form-field appearance="horizontal" class="[&>div>div:first-child]:max-w-[96px]" [config]="{ label: 'Title' }">
        <vs-input-container>
          <input
            required
            type="text"
            minlength="1"
            formControlName="title"
            placeholder="Please write a title for your document"
          />
          <vs-badge *ngIf="extension as ext" theme="info" slot="end">{{ ext }}</vs-badge>
        </vs-input-container>
      </vs-form-field>

      <vs-form-field
        appearance="horizontal"
        class="[&>div>div:first-child]:max-w-[96px]"
        [config]="{ label: 'Description' }"
      >
        <textarea
          vsInput
          vsAutosize
          rows="2"
          minRows="2"
          maxRows="4"
          formControlName="description"
          placeholder="Please write a description (optional)"
        ></textarea>
      </vs-form-field>

      <div vsInputBox class="h-24 px-6 py-4 flex items-center gap-6">
        <div class="h-full aspect-square rounded-md overflow-hidden border border-gray-300">
          <img
            #img
            *ngIf="document.downloadUrl"
            class="w-full h-full object-cover"
            [src]="document.downloadUrl | safe: 'url'"
            (error)="img.style.display = 'none'; otherFileType.style.display = 'flex'"
            (loaded)="img.style.display = 'unset'; otherFileType.style.display = 'none'"
          />

          <div #otherFileType class="w-full h-full items-center justify-center flex">
            <vs-icon name="file-attachment-02" size="32" class="text-gray-600"></vs-icon>
          </div>
        </div>
        <div
          class="flex-1 text-xs text-gray-600 truncate"
          [vsTooltip]="filename ?? undefined"
          tooltipOpenDelay="350"
          tooltipStyle="light"
        >
          {{ filename }}
        </div>
      </div>
    </form>

    <div class="mt-8 grid grid-cols-[1fr_1fr] sm:grid-cols-[1fr_auto_auto] gap-3">
      <vs-input-group *ngIf="canDelete$ | async" class="w-full sm:w-fit col-span-2 sm:col-span-1">
        <button
          vs-button
          class="flex-1"
          appearance="outline"
          [class.!opacity-100]="confirmDelete || deleting"
          [disabled]="confirmDelete || deleting || saving"
          (click)="confirmDelete = true"
        >
          {{ confirmDelete ? 'Are you sure?' : '' }}
          <div *ngIf="!confirmDelete" class="flex items-center">
            <svg
              class="text-themed-700 h-4 transition-[width,margin] {{deleting ? 'animate-spin w-4 mr-2' : 'w-0 mr-0'}}"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <div>Delet</div>
            <div class="transition-[width] overflow-hidden {{deleting ? 'w-6' : 'w-2.5'}}">
              {{ deleting ? 'ing' : 'e'}}
            </div>
          </div>
        </button>
        <button
          *ngIf="confirmDelete"
          vs-icon-button
          appearance="outline"
          class="group"
          [disabled]="deleting || saving"
          (click)="confirmDelete = false"
        >
          <vs-icon size="20" name="x-close" class="text-danger group-hover:text-danger-700"></vs-icon>
        </button>
        <button
          *ngIf="confirmDelete"
          vs-icon-button
          appearance="outline"
          class="group"
          [disabled]="deleting || saving"
          (click)="deleteDocument()"
        >
          <vs-icon size="20" name="check" class="text-success group-hover:text-success-700"></vs-icon>
        </button>
      </vs-input-group>

      <button vs-button appearance="outline" class="w-full" [disabled]="saving || deleting" (click)="close()">
        Cancel
      </button>

      <button
        *ngIf="canUpdate$ | async"
        vs-button
        theme="primary"
        class="w-full"
        [class.!opacity-100]="saving"
        [disabled]="pristine || form.invalid || saving || deleting"
        (click)="save()"
      >
        <div class="flex items-center">
          <svg
            class="text-base h-4 transition-[width,margin] {{saving ? 'animate-spin w-4 mr-2' : 'w-0 mr-0'}}"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <div>Sav</div>
          <div class="transition-[width] overflow-hidden {{saving ? 'w-6' : 'w-2.5'}}">{{ saving ? 'ing' : 'e'}}</div>
        </div>
      </button>
    </div>
  </div>
</vs-dialog>
